import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";
import { Loader2, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/shared/ui/form";
import { toast } from "@/shared/hooks/use-toast";
import { profileService } from "@/entities/profile";
import { makeForcePasswordSchema } from "./forcePasswordSchema";
import { errorToast } from "@/shared/lib/apiErrors";

type Values = z.infer<ReturnType<typeof makeForcePasswordSchema>>;

/**
 * 首登强制改密弹窗(2026-08-09)。
 *
 * 初始密码(服务端随机发号,或存量账号的老值 123456)只靠建号弹窗提醒 —— 忘了就一直裸奔。后端登录响应带
 * `requirePasswordChange` 时,LoginPage 挂出本弹窗:**不可关闭**(无叉、点外面不散),
 * 改完密码才 navigate 进站。token 已落地,改密走既有 `PUT /me/password`。
 */
export function ForcePasswordChangeDialog({
  oldPassword,
  onDone,
}: {
  oldPassword: string;
  /** 改密成功后回调;带上新密码 —— 后端改密会踢会话,调用方要拿它重新登录。 */
  onDone: (newPassword: string) => void | Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const schema = useMemo(() => makeForcePasswordSchema(oldPassword), [oldPassword]);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { newPassword: "", confirm: "" },
  });

  const submit = async (values: Values) => {
    setSubmitting(true);
    try {
      await profileService.changePassword(oldPassword, values.newPassword);
      toast({ title: "密码已更新", description: "以后请用新密码登录。" });
      await onDone(values.newPassword);
    } catch (err) {
      toast(errorToast(err, { fallbackTitle: "修改失败" }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open>
      {/* 不给 onOpenChange:Esc / 点遮罩都不关 —— 改完密码是唯一出口 */}
      <DialogContent className="max-w-sm [&>button]:hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-warning" />
            请先设置新密码
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          当前密码是管理员发给你的初始密码,转交过程中可能被别人看到。设置一个新密码后进入系统。
        </p>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="space-y-4">
            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>新密码</FormLabel>
                  <FormControl>
                    <Input type="password" autoFocus autoComplete="new-password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirm"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>再输一遍</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="new-password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                保存并进入系统
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
